import { useEffect, useState } from 'react';
import Card from '../../../components/atoms/card';
import Select from '../../../components/atoms/select';
import Text from '../../../components/atoms/text';
import { getLocale, languageOptions, Locale } from '../../../lib/locale';
import { useTranslation } from 'react-i18next';
import SVGIcon from '../../../components/atoms/icons';
import Modal from '../../../components/organisms/modal';

const Help = () => {
	const { t } = useTranslation();

	const [currentLanguage, setCurrentLanguage] = useState(
		() =>
			languageOptions.find((lang) => lang.key === getLocale()) ??
			languageOptions[0]
	);
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [versaoManual, definirVersaoManual] = useState('V3');

	const handleLanguageChange = (selectedLabel: string) => {
		const selectedLanguage = languageOptions.find(
			(lang) => lang.label === selectedLabel
		);
		if (selectedLanguage) setCurrentLanguage(selectedLanguage);
	};

	const handleModalVisibility = (visible: boolean) =>
		setIsModalVisible(visible);

	const renderManualTemplate = (
		color: 'primary-light' | 'primary',
		file: string,
		title: string
	) => {
		// Mapear as cores para classes Tailwind
		const colorClasses = {
			'primary-light': {
				bg: 'bg-primary-light',
				text: 'text-primary-light',
				border: 'border-primary-light',
			},
			primary: {
				bg: 'bg-primary',
				text: 'text-primary',
				border: 'border-primary',
			},
		};

		const classes = colorClasses[color] || colorClasses['primary'];

		return (
			<Card className="flex-grow flex flex-col border-default cursor-pointer text-white overflow-hidden">
				<a href={`/${file}`} download={file.split('/').pop()}>
					<div className="flex flex-col w-48 justify-self-center mt-8 mb-8">
						<Text
							className={`text-center ${classes.bg} font-semibold`}
						>
							{t('help.instruction_manual')}
						</Text>
						<div className="flex justify-between">
							<Text
								className={`w-min ${classes.text} leading-none font-all-small-caps font-semibold px-4 py-1`}
							>
								{t('help.container_management')}
							</Text>
							<div
								className={`border-2 border-t-0 ${classes.border} size-[4.5rem] flex items-center justify-center`}
							>
								<SVGIcon
									className="w-10 h-auto "
									icon="LogoR"
								/>
							</div>
						</div>
					</div>
					<div
						className={`flex-grow ${classes.bg} flex flex-col justify-between p-4`}
					>
						<Text as="h3" className="text-center">
							ReeferSys
						</Text>
						<Text className="text-lg text-center font-all-small-caps font-semibold mt-8 mb-8">
							{title}
						</Text>
						<div className="flex justify-between items-end">
							<SVGIcon className="w-28 h-auto" icon="Logo" />
							<Text className="font-all-small-caps font-semibold">
								{versaoManual === 'V2' ? 'REV 02.000.00' : 'REV 03.000.00'}
							</Text>
						</div>
					</div>
				</a>
			</Card>
		);
	};

	return (
		<>
			<Card className="flex-grow flex flex-col gap-4 p-4">
				<div className="flex flex-col gap-2">
					<Text
						className="text-primary flex gap-2 items-center"
						as="h4"
					>
						{t('help.download_manual')}
						{versaoManual === 'V2' ? (
							<Text
								className="relative size-5 group cursor-pointer flex items-center justify-center"
								onClick={() => handleModalVisibility(true)}
							>
								<SVGIcon
									icon={currentLanguage.icon}
									className="absolute w-5 h-auto group-hover:opacity-40"
								/>
								<SVGIcon
									icon="Pencil"
									className="absolute w-3 h-auto invisible group-hover:visible"
								/>
							</Text>
						) : (
							<Text className="flex gap-2 items-center text-sm">
								<SVGIcon icon="FlagBR" className="w-5 h-auto" />
								Português
							</Text>
						)}
					</Text>
					<Select
						className="w-48"
						name="versaoManual"
						options={['V2', 'V3']}
						value={versaoManual}
						onChange={(e) => definirVersaoManual(e.target.value)}
					/>
					<div className="flex-grow flex flex-wrap gap-12">
						{versaoManual === 'V2' ? (
							<>
								{renderManualTemplate(
									'primary-light',
									`reefersys_manual_web_${currentLanguage.key}.pdf`,
									t('help.basic_manual_web')
								)}
								{renderManualTemplate(
									'primary',
									`reefersys_manual_mobile_${currentLanguage.key}.pdf`,
									t('help.basic_manual_mobile')
								)}
							</>
						) : (
							<>
								{renderManualTemplate(
									'primary-light',
									'manuais-v3/Manual_de_Uso_ReeferSys_Web.pdf',
									'Manual de Uso Web'
								)}
								{renderManualTemplate(
									'primary',
									'manuais-v3/Manual_do_Aplicativo_ReeferSys.pdf',
									'Manual do Aplicativo'
								)}
								{renderManualTemplate(
									'primary-light',
									'manuais-v3/Manual_do_Painel_do_Cliente_ReeferSys.pdf',
									'Manual do Painel do Cliente'
								)}
								{renderManualTemplate(
									'primary',
									'manuais-v3/Manual_Container_Tracking_ReeferSys.pdf',
									'Manual Container Tracking'
								)}
							</>
						)}
					</div>
				</div>
			</Card>
			<Modal
				className="gap-3"
				isModalVisible={isModalVisible}
				onClose={() => handleModalVisibility(false)}
				title={t('help.manual_language')}
			>
				<div className="flex flex-col gap-2">
					<Select
						className="w-48"
						name="language"
						options={languageOptions.map((lang) => lang.label)}
						icons={languageOptions.map((lang) => lang.icon)}
						value={currentLanguage.label}
						onChange={(e) => handleLanguageChange(e.target.value)}
						title={t('locale.select')}
					/>
				</div>
			</Modal>
		</>
	);
};

export default Help;
